import type { Hubble } from '@seek/hubble/lib/Hubble';
import { JOB_LISTING_POSITION_KEY } from '@seek/job-details-view';
import { metrics } from '@seek/metrics-js';
import { isHomepagePath, isSearchResultsPath } from '@seek/seek-jobs-seo';
import { matchRoutes, type LoaderFunction } from 'react-router';

import {
  REFINE_BAR_V2_FEATURE_FLAG,
  RefineBarV2,
  StickySearchBar,
  type RefineBarV2Experiment,
} from 'src/config/experiments.ts';
import type { AnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { setHubbleLoginId } from 'src/modules/hubble';
import { logger } from 'src/modules/logger';
import { parseCookies } from 'src/modules/safe-cookies';
import {
  getCandidateUserIdFromTokenIdentity,
  isAuthenticated,
} from 'src/modules/seek-jobs-api-client/apis/candidate';
import pathnameToPageName from 'src/pathnameToPageName';
import appRoutesConfig from 'src/routes';
import type createStore from 'src/store/createStore';
import { clearJobDetails } from 'src/store/jobdetails';
import { locationChanged } from 'src/store/location';
import { clearNewSince } from 'src/store/results';
import {
  selectAuthenticated,
  selectIsJDPSplitView,
  selectLocation,
  selectServerAuthenticated,
  selectSolId,
} from 'src/store/selectors';
import { setSeekerId, updateAuthenticated } from 'src/store/user';
import { getFeatureFlagWithOverridesHelper } from 'src/utils/getFeatureFlagWithOverrides';
import { initialiseSignedInExperiments } from 'src/utils/initialiseSignedInExperiments';

import { fetchDataForRoutes } from './fetchDataForRoutes';
import { routerRequestToUrlLocation } from './locationTransforms';

const ALLOWED_QUERY_KEYS = [
  'tags',
  'jobId',
  'type',
  JOB_LISTING_POSITION_KEY,
  'page',
];

const fireAuthMetric = ({
  event,
  clientAuthenticated,
  serverAuthenticated,
}: {
  event: string;
  clientAuthenticated: boolean;
  serverAuthenticated: boolean;
}) => {
  metrics.count('auth.event', [
    `event:${event}`,
    `csa:${clientAuthenticated}`,
    `ssa:${serverAuthenticated}`,
  ]);
};

/** Returns an array of keys whose values changed between prevQuery and nextQuery */
function findChangedQueryParams(
  prevQuery: Record<string, string | undefined>,
  nextQuery: Record<string, string | undefined>,
): string[] {
  const allKeys = new Set([
    ...Object.keys(prevQuery || {}),
    ...Object.keys(nextQuery || {}),
  ]);
  const changedKeys: string[] = [];

  for (const key of allKeys) {
    if (prevQuery[key] !== nextQuery[key]) {
      changedKeys.push(key);
    }
  }
  return changedKeys;
}

/** Returns true if *every* changed key is in the ALLOWED_QUERY_KEYS list */
function onlyAllowedKeysChanged(changedKeys: string[]): boolean {
  return changedKeys.every((key) => ALLOWED_QUERY_KEYS.includes(key));
}

interface Options {
  analyticsFacade: AnalyticsFacade;
  store: ReturnType<typeof createStore>;
  apolloClient: Parameters<typeof fetchDataForRoutes>[0]['apolloClient'];
  visitorId: string;
  hubble: Hubble;
}

type LoaderFunctionContext = {
  initialLoad?: boolean;
};

const syncSeekerIdAndSignedInExperiments = async ({
  store,
  analyticsFacade,
}: {
  store: ReturnType<typeof createStore>;
  analyticsFacade: AnalyticsFacade;
}) => {
  const { getState, dispatch } = store;
  const {
    appConfig: { zone },
    user: { isLoggedInExperimentsReady },
  } = getState();

  if (!isLoggedInExperimentsReady) {
    const seekerId = await getCandidateUserIdFromTokenIdentity();
    if (seekerId) {
      dispatch(setSeekerId(seekerId));
      analyticsFacade.userDetailsUpdated({
        authenticated: true,
        loginId: String(seekerId),
      });

      initialiseSignedInExperiments({
        seekerId,
        zone,
        store,
        cookies: parseCookies(),
        analyticsFacade,
      });
    }
  }
};

export const createRouteLoaderFunction = (
  options: Options,
): LoaderFunction<LoaderFunctionContext> => {
  const { analyticsFacade, store, apolloClient, visitorId, hubble } = options;
  const { dispatch, getState } = store;
  const getFeatureFlag = getFeatureFlagWithOverridesHelper({
    apolloClient,
    getState,
  });

  const loaderFunction: LoaderFunction<LoaderFunctionContext> = async ({
    request,
    context,
  }) => {
    const state = store.getState();
    const currentLocation = selectLocation(state);
    const nextLocation = routerRequestToUrlLocation(request);
    metrics.addGlobalTags([
      `page:${pathnameToPageName(nextLocation.pathname!)}`,
    ]);
    metrics.count('pageload');

    const refineBarV2Feature = getFeatureFlag<RefineBarV2Experiment>(
      REFINE_BAR_V2_FEATURE_FLAG,
    );

    const refineBarV2 = refineBarV2Feature === RefineBarV2;

    const stickySearchBar = refineBarV2Feature === StickySearchBar;

    const { pathname: currentPathname } = currentLocation;
    const { pathname: nextPathname } = nextLocation;

    const isFromHomepage = isHomepagePath(currentPathname);
    const isToHomepage = isHomepagePath(nextPathname || '');
    const isToSrp = isSearchResultsPath(nextPathname || '');
    // Navigating to homepage is handled using ScrollRestoration
    // component, so we don't need to scroll upon navigating to it
    if (!isToHomepage) {
      if ((refineBarV2 || stickySearchBar) && isToSrp && !isFromHomepage) {
        // If context?.initialLoad is true, this is most likely a refresh or external redirect so let the browser handle it
        // A refresh should scroll to the last scrollLocation, an external redirect should open from the top (SEEK header should be visible)
        if (!context?.initialLoad) {
          // If we're already on the SRP page and run a search, we should automatically scroll the page
          // so the SEEK header is not visible and the search bar is at the top
          const scrollIntoElement =
            document.querySelector('div[role="main"]') || window.document.body;
          scrollIntoElement.scrollIntoView();
        }
      } else {
        window.document.body.scrollIntoView();
      }
    }

    /*
     * Compare the entire query objects to see if
     * *some* keys changed that are *not* in ALLOWED_QUERY_KEYS.
     * Also, if the pathname changed, always clear the stickiness
     */
    const changedKeys = findChangedQueryParams(
      currentLocation.query ?? {},
      nextLocation.query ?? {},
    );
    const pathnameChanged = currentPathname !== nextPathname;
    if (pathnameChanged) {
      // If the pathname changes, we lose stickiness in search
      dispatch(clearNewSince());
    } else if (changedKeys.length > 0 && !onlyAllowedKeysChanged(changedKeys)) {
      // If there are changed keys and any of them is disallowed, clear
      dispatch(clearNewSince());
    }

    const isNavigatingWithinSRP =
      isSearchResultsPath(currentPathname) && isToSrp;
    const isJobLoadedInSplitView = selectIsJDPSplitView(state);
    if (isNavigatingWithinSRP && isJobLoadedInSplitView) {
      // Always clear the loaded job in SplitView when making a new search or navigating within SRP
      // We need to clear it earlier here to avoid the job_details_displayed tracking event firing unexpectedly
      dispatch(clearJobDetails());
    }

    // Continue with the usual route-matching logic
    const matchedRoutes = matchRoutes(
      appRoutesConfig(state.appConfig.site),
      nextPathname!,
    )?.map(({ route }) => route);
    const authenticated = selectAuthenticated(state);

    dispatch(locationChanged(nextLocation.href!, nextLocation));

    if (context?.initialLoad) {
      await syncSeekerIdAndSignedInExperiments({ store, analyticsFacade });

      // Logging mismatch between cookie sol_id and client sol_id
      if (selectSolId(state) !== hubble.visitorId()) {
        metrics.count('solId.event', ['mismatch:true']);
      }

      const isServerAuthenticated = selectServerAuthenticated(state);
      const isClientAuthenticated = await isAuthenticated();
      fireAuthMetric({
        event: 'authMismatch',
        clientAuthenticated: isClientAuthenticated,
        serverAuthenticated: isServerAuthenticated,
      });
    } else {
      /*
        If this handler is called during page load, we do not want
        to call the location changed analytics event as it will prematurely
        populate incorrect data layer properties (eg. previousSearchId).
        This is a stop gap solution in response to https://myseek.atlassian.net/browse/DCS-7030.

        We are using this short term solution as refactoring the boot sequence is a
        much bigger undertaking that requires a deeper investigation. An investigation ticket
        has been created here: https://myseek.atlassian.net/browse/DCS-7331
      */
      analyticsFacade.locationChanged({ pathname: nextPathname! });
    }

    const waitForAuth = async () => {
      if (!authenticated) {
        const resolvedAuthState = await isAuthenticated();
        fireAuthMetric({
          event: 'lastKnownSolIdNotSet',
          clientAuthenticated: resolvedAuthState,
          serverAuthenticated: false, // We know the user is not logged in on the server
        });
        const payload = { authenticated: resolvedAuthState };
        dispatch(updateAuthenticated(payload));
        analyticsFacade.userDetailsUpdated(payload);
        setHubbleLoginId(hubble, store);
        return resolvedAuthState;
      }
      return authenticated;
    };

    fetchDataForRoutes({
      analyticsFacade,
      matchedRoutes,
      location: nextLocation,
      dispatch,
      getState: store.getState,
      apolloClient,
      waitForAuth,
      visitorId,
    }).catch((err: Error) => {
      logger.error(err);
    });
  };

  return loaderFunction;
};
